import moment from 'moment';
import { TITLE } from '@/store/models/profile';

export const getAgentTitle = value => {
  return TITLE.find(el => el.value === value)?.label;
};

export const getPhoneNumber = ({ cell_phone, phone, phone_ext }) => {
  if (!cell_phone && !phone) return null;
  if (cell_phone) {
    return { label: cell_phone, anchor: `+1${cell_phone.replace(/-/g, '')}` };
  }
  if (phone) {
    return phone_ext
      ? { label: `${phone} x${phone_ext}`, anchor: `+1${phone.replace(/-/g, '')};ext=${phone_ext.replace(/-/g, '')}` }
      : { label: phone, anchor: `+1${phone.replace(/-/g, '')}` };
  }
};

export const getLanguages = languages_spoken => {
  return languages_spoken?.length ? languages_spoken.join(', ') : 'English';
};

export const getFullAddress = ({ address_1, address_2, city, state, zip_code }) => {
  const lineOne = `${address_1 || ''}${address_2 ? ',' : ''} ${address_2 || ''}`.trim();
  const lineTwo = `${city || ''}${city ? ',' : ''} ${state || ''} ${zip_code || ''}`.trim();
  return [lineOne, lineTwo];
};

export const getMapQuery = ({ address_1, city, state, zip_code }) => {
  const parts = [address_1, city, state, zip_code].filter(Boolean);
  return parts.length >= 2 ? encodeURIComponent(parts.join(', ')) : null;
};

export const getOfficeHours = office_hours => {
  if (!office_hours?.length) return [];

  const DAYS = Object.freeze(['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun']);
  const getDayIndex = day => DAYS.indexOf(day);
  const convertTo12HourFormat = time => moment(time, 'HH:mm').format('h:mma');

  const sortDays = officeHours => {
    return officeHours.map(entry => ({
      days: [...entry.days].sort((a, b) => getDayIndex(a) - getDayIndex(b)),
      timeRange: { ...entry.timeRange }
    }));
  };

  const splitSequentialDays = schedule => {
    const { days, timeRange } = schedule;
    let result = [];
    let tempArray = [days[0]];

    for (let i = 1; i < days.length; i++) {
      if (getDayIndex(days[i]) === getDayIndex(days[i - 1]) + 1) {
        tempArray.push(days[i]);
      } else {
        result.push(tempArray);
        tempArray = [days[i]];
      }
    }
    result.push(tempArray);
    return { days: result.length === 1 ? days : result, timeRange };
  };

  const groupOfficeHours = schedule => {
    const result = [];
    schedule.forEach(entry => {
      const groupedDays = splitSequentialDays(entry);
      if (groupedDays.days.every(group => Array.isArray(group))) {
        groupedDays.days.forEach(group => {
          result.push({ days: group, timeRange: groupedDays.timeRange });
        });
      } else {
        result.push(groupedDays);
      }
    });
    return result;
  };

  const addClosedDays = schedule => {
    const closedDays = { days: [], timeRange: { start: '', end: '' } };
    const allDaysListed = [...new Set(schedule.map(entry => entry.days).flat())];
    DAYS.forEach(day => {
      if (!allDaysListed.includes(day)) closedDays.days.push(day);
    });
    return groupOfficeHours([closedDays]);
  };

  const sortMerged = schedule => {
    const dayIndex = DAYS.reduce((acc, day, index) => {
      acc[day] = index;
      return acc;
    }, {});
    const timeToMoment = time => (time ? moment(time, 'HH:mm') : moment('23:59', 'HH:mm'));

    return schedule.sort((a, b) => {
      const firstDayA = dayIndex[a.days[0]];
      const firstDayB = dayIndex[b.days[0]];
      if (firstDayA !== firstDayB) return firstDayA - firstDayB;
      const lengthDifference = b.days.length - a.days.length;
      if (lengthDifference !== 0) return lengthDifference;
      return timeToMoment(a.timeRange.start) - timeToMoment(b.timeRange.start);
    });
  };

  const formatOfficeHours = schedule => {
    return schedule.map(entry => {
      const isClosed = !entry.timeRange.start && !entry.timeRange.end;
      const days = entry.days.length > 1 ? `${entry.days[0]} - ${entry.days[entry.days.length - 1]}` : entry.days[0];
      const timeRange = isClosed
        ? 'Closed'
        : `${convertTo12HourFormat(entry.timeRange.start)} – ${convertTo12HourFormat(entry.timeRange.end)}`;
      return { days, timeRange };
    });
  };

  const grouped = groupOfficeHours(sortDays(office_hours));
  const mergedWithClosed = [...grouped, ...addClosedDays(grouped)];
  const sorted = sortMerged(mergedWithClosed);
  return formatOfficeHours(sorted);
};
